import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { 
  Box,
  Typography,
  Button,
  Paper,
  Avatar,
  Chip,
  Divider,
  Stack,
  IconButton
} from "@mui/material";
import { QRCodeCanvas } from "qrcode.react";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import EventIcon from '@mui/icons-material/Event';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import DescriptionIcon from '@mui/icons-material/Description';
import logo from "../cosmologo.png";
import { ThemeProvider, createTheme } from '@mui/material/styles';

// Custom theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#013a64',
    },
    secondary: {
      main: '#4CAF50',
    },
    error: {
      main: '#f44336',
    },
    warning: {
      main: '#ff9800',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h5: {
      fontWeight: 600,
    },
  },
});

const RequestDetailScreen = () => {
  const location = useLocation();
  const { request } = location.state || {};
  const navigate = useNavigate();

  const safeValue = (value) => (value ? value.toString() : "Not specified");

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Approved':
        return <CheckCircleIcon color="success" />;
      case 'Pending':
        return <PendingIcon color="warning" />;
      case 'Rejected':
        return <CancelIcon color="error" />;
      default:
        return <PendingIcon color="warning" />;
    }
  };

  const formatDateTime = (dateString, timeString) => {
    if (!dateString) return "Not specified";
    const timePart = timeString ? `, ${timeString}` : "";
    return `${dateString}${timePart}`;
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        minHeight: '100vh',
        backgroundColor: '#f5f7fa'
      }}>
        {/* Header */}
        <Box sx={{ 
          backgroundColor: 'primary.main',
          color: 'white',
          p: 2,
          display: 'flex',
          alignItems: 'center'
        }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate(-1)}
            sx={{ mr: 1 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Back
          </Typography>
        </Box>

        {/* Main Content */}
        <Box sx={{ 
          flex: 1, 
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <Paper elevation={3} sx={{ 
            p: 3, 
            width: '100%',
            maxWidth: '600px',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            {/* Logo */}
            <Avatar
              src={logo}
              alt="Logo"
              sx={{ 
                width: 100, 
                height: 100, 
                mb: 3,
                boxShadow: 2
              }}
            />

            {/* Status Chip */}
            {request?.status && (
              <Chip
                label={request.status}
                icon={getStatusIcon(request.status)}
                color={
                  request.status === 'Approved' ? 'success' :
                  request.status === 'Rejected' ? 'error' : 'warning'
                }
                variant="outlined"
                sx={{ mb: 3, px: 2, py: 1 }}
              />
            )}

            <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main' }}>
              Pass Request Details
            </Typography>

            {/* Details List */}
            <Stack spacing={2} sx={{ width: '100%', mb: 3 }}>
              <Divider />
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EventIcon color="action" sx={{ mr: 2 }} />
                <Typography variant="body1" sx={{ flex: 1 }}>
                  <strong>Date:</strong> {safeValue(request?.date)}
                </Typography>
              </Box>
              
              <Divider />
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ScheduleIcon color="action" sx={{ mr: 2 }} />
                <Typography variant="body1" sx={{ flex: 1 }}>
                  <strong>Time:</strong> {safeValue(request?.time)}
                </Typography>
              </Box>
              
              <Divider />
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ExitToAppIcon color="action" sx={{ mr: 2 }} />
                <Typography variant="body1" sx={{ flex: 1 }}>
                  <strong>Exit Type:</strong> {safeValue(request?.exitType)}
                </Typography>
              </Box>
              
              <Divider />
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <DescriptionIcon color="action" sx={{ mr: 2, mt: 0.5 }} />
                <Typography variant="body1" sx={{ flex: 1 }}>
                  <strong>Purpose:</strong> {safeValue(request?.purpose)}
                </Typography>
              </Box>
              
              <Divider />
            </Stack>

            {/* QR Code for approved requests */}
            {request?.status === "Approved" && (
              <Box sx={{ 
                textAlign: 'center',
                mt: 2,
                p: 3,
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 2
              }}>
                <Typography variant="subtitle1" gutterBottom>
                  Scan this QR code for verification
                </Typography>
                <QRCodeCanvas 
                  value={safeValue(request?.requestId)} 
                  size={180}
                  level="H"
                  includeMargin={true}
                />
                
              </Box>
            )}
          </Paper>

          {/* Back Button */}
          <Button
            variant="contained"
            onClick={() => navigate(-1)}
            sx={{
              mt: 3,
              px: 4,
              py: 1.5,
              borderRadius: 1,
              fontWeight: 'bold',
              textTransform: 'none',
              fontSize: '1rem'
            }}
          >
            Back
          </Button>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default RequestDetailScreen;